import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import adminApi from "../../connection/adminApi";
import AdminLayout from "./AdminLayout";
import PurgeStoreButton from "./PurgeStoreButton";
import LoginAsButton from "./LoginAsButton";
import { StoreVectorDot } from "./VectorStoreStatus";
import {
  ColumnWidths,
  ResizeHandle,
  useResizableColumns,
} from "./ResizableColumns";

// Percentages, so the table always fills its box exactly. Drag a header edge
// to move space between two neighbours; the total never changes.
const COLUMNS = [
  { key: "store", label: "Store", width: 20 },
  { key: "owner", label: "Owner", width: 18 },
  { key: "plan", label: "Plan", width: 12 },
  { key: "status", label: "Status", width: 12 },
  { key: "lastLogin", label: "Last admin login", width: 15 },
  { key: "registered", label: "Registered", width: 12 },
  { key: "actions", label: "", width: 11 },
];

const COLUMN_STORAGE_KEY = "storakAdminStoreColumns";

const PLAN_COLORS = {
  launch: "gray",
  growth: "blue",
  galaxy: "purple",
  enterprise: "orange",
};

const STATUS_COLORS = {
  active: "green",
  trialing: "red",
  past_due: "orange",
  canceled: "gray",
  incomplete: "yellow",
};

/** How recently someone signed in says more than the raw timestamp. */
const relativeDays = (value) => {
  if (!value) {
    return null;
  }

  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);

  if (days <= 0) {
    return { label: "today", color: "green.600" };
  }
  if (days === 1) {
    return { label: "yesterday", color: "green.600" };
  }
  if (days < 30) {
    return { label: `${days} days ago`, color: "gray.600" };
  }
  if (days < 365) {
    return { label: `${Math.floor(days / 30)} months ago`, color: "orange.600" };
  }

  return { label: "over a year ago", color: "red.500" };
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminStores = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;
  const search = searchParams.get("search") || "";

  const { widths, tableRef, startResize, reset } = useResizableColumns(
    COLUMN_STORAGE_KEY,
    COLUMNS
  );

  const [term, setTerm] = useState(search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await adminApi.get("api/admin/stores", {
        params: { page, limit, search: search || undefined },
      });
      setData(response.data);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message || "Could not load the stores"
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setTerm(search);
  }, [search]);

  const applySearch = (event) => {
    event.preventDefault();
    const next = { page: "1", limit: String(limit) };

    if (term.trim()) {
      next.search = term.trim();
    }

    setSearchParams(next);
  };

  const goToPage = (next) => {
    const params = { page: String(next), limit: String(limit) };

    if (search) {
      params.search = search;
    }

    setSearchParams(params);
  };

  return (
    <AdminLayout>
      <Flex align="center" mb={4} gap={4} wrap="wrap">
        <Heading size="md">Stores</Heading>
        {data ? (
          <Text color="gray.500" fontSize="sm">
            {data.total} registered
          </Text>
        ) : null}

        <Box flex="1" />

        <form onSubmit={applySearch}>
          <HStack>
            <InputGroup size="sm" width="280px">
              <Input
                bg="white"
                borderRadius="md"
                placeholder="Search name, username, email, database"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
              />
              <InputRightElement>
                <SearchIcon color="gray.400" boxSize={3} />
              </InputRightElement>
            </InputGroup>

            <Button size="sm" type="submit">
              Search
            </Button>

            {search ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSearchParams({ page: "1", limit: String(limit) })}
              >
                Clear
              </Button>
            ) : null}

            <Select
              size="sm"
              bg="white"
              width="90px"
              value={limit}
              onChange={(event) => {
                const params = { page: "1", limit: event.target.value };
                if (search) {
                  params.search = search;
                }
                setSearchParams(params);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
          </HStack>
        </form>
      </Flex>

      {error ? (
        <Box bg="red.50" color="red.700" p={3} borderRadius="md" mb={4} fontSize="sm">
          {error}
        </Box>
      ) : null}

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="0 1px 3px rgba(16,24,40,.08), 0 1px 2px rgba(16,24,40,.06)"
        borderWidth="1px"
        borderColor="gray.100"
        overflow="hidden"
      >
        <Box overflowX="hidden">
          <Table
            ref={tableRef}
            size="md"
            variant="simple"
            width="100%"
            sx={{
              tableLayout: "fixed",
              // Chakra's TableContainer would keep cells on one line and scroll
              // the table sideways; fixed columns wrap or ellipsis instead.
              "& td, & th": { whiteSpace: "normal", overflow: "hidden" },
            }}
          >
            <ColumnWidths widths={widths} />
            <Thead bg="gray.50">
              <Tr>
                {COLUMNS.map((column, index) => (
                  <Th
                    key={column.key}
                    position="relative"
                    fontSize="10px"
                    letterSpacing="0.08em"
                    color="gray.500"
                    borderColor="gray.100"
                    py={3}
                    title={
                      index < COLUMNS.length - 1
                        ? "Drag the edge to resize · double-click to reset"
                        : undefined
                    }
                    onDoubleClick={index < COLUMNS.length - 1 ? reset : undefined}
                  >
                    {column.label}
                    {index < COLUMNS.length - 1 ? (
                      <ResizeHandle onMouseDown={startResize(index)} />
                    ) : null}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {loading
                ? [...Array(6)].map((_, index) => (
                    <Tr key={index}>
                      <Td colSpan={7} borderColor="gray.100">
                        <Skeleton height="22px" borderRadius="md" />
                      </Td>
                    </Tr>
                  ))
                : (data?.items || []).map((store) => {
                    const seen = relativeDays(store.lastLoginAt);

                    return (
                      <Tr
                        key={store.id}
                        transition="background .12s ease"
                        _hover={{ bg: "blue.50", cursor: "pointer" }}
                        onClick={() => navigate(`/admin/stores/${store.id}`)}
                      >
                        <Td borderColor="gray.100">
                          <HStack spacing={3}>
                            <Avatar
                              size="sm"
                              name={store.name || store.username || "?"}
                              bg="blue.500"
                              color="white"
                              fontWeight="700"
                            />
                            <Box minW={0}>
                              <HStack spacing={2} minW={0}>
                                <Text fontWeight="600" noOfLines={1}>
                                  {store.username || "—"}
                                </Text>
                                <StoreVectorDot storeId={store.id} />
                              </HStack>
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                {store.databaseName}
                              </Text>
                            </Box>
                          </HStack>
                        </Td>
                        <Td borderColor="gray.100">
                          <Text fontSize="sm" noOfLines={1}>
                            {store.name || "—"}
                          </Text>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {store.email}
                          </Text>
                        </Td>
                        <Td borderColor="gray.100">
                          <Badge
                            colorScheme={PLAN_COLORS[store.plan?.code] || "gray"}
                            borderRadius="full"
                            px={2.5}
                            py={0.5}
                            textTransform="none"
                            fontWeight="600"
                          >
                            {store.plan?.nameEn || "none"}
                          </Badge>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {(store.region || "—").toUpperCase()} ·{" "}
                            {store.interval || "—"}
                          </Text>
                        </Td>
                        <Td borderColor="gray.100">
                          <Badge
                            colorScheme={STATUS_COLORS[store.status] || "gray"}
                            variant={
                              store.status === "trialing" ? "solid" : "subtle"
                            }
                            borderRadius="full"
                            px={2.5}
                            py={0.5}
                            textTransform="none"
                            fontWeight="600"
                          >
                            {store.status || "unknown"}
                          </Badge>
                          {store.hasBilling ? null : (
                            <Tooltip label="No Stripe customer on file">
                              <Text fontSize="xs" color="orange.500" mt={1}>
                                no billing
                              </Text>
                            </Tooltip>
                          )}
                        </Td>
                        <Td borderColor="gray.100">
                          <Text fontSize="sm">{formatDate(store.lastLoginAt)}</Text>
                          {seen ? (
                            <Text fontSize="xs" color={seen.color}>
                              {seen.label}
                            </Text>
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              never signed in
                            </Text>
                          )}
                        </Td>
                        <Td borderColor="gray.100" fontSize="sm" color="gray.600">
                          {formatDate(store.createdAt)}
                        </Td>
                        <Td
                          borderColor="gray.100"
                          textAlign="right"
                          // The row navigates on click; the actions must not.
                          onClick={(event) => event.stopPropagation()}
                        >
                          {/* No "Open" button: clicking the row already goes
                              to the store. */}
                          <HStack spacing={1} justify="flex-end">
                            <LoginAsButton
                              storeId={store.id}
                              size="sm"
                              variant="ghost"
                              label="Log in as admin"
                              iconOnly
                            />
                            <PurgeStoreButton
                              store={store}
                              size="sm"
                              iconOnly
                              onPurged={load}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}

              {!loading && !(data?.items || []).length ? (
                <Tr>
                  <Td colSpan={7} borderColor="gray.100">
                    <Text py={10} textAlign="center" color="gray.500">
                      No store matches this search.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {data && data.pages > 1 ? (
        <Flex mt={4} align="center" gap={3}>
          <Button
            size="sm"
            variant="outline"
            isDisabled={page <= 1}
            onClick={() => goToPage(page - 1)}
          >
            Previous
          </Button>
          <Text fontSize="sm" color="gray.600">
            Page {data.page} of {data.pages}
          </Text>
          <Button
            size="sm"
            variant="outline"
            isDisabled={page >= data.pages}
            onClick={() => goToPage(page + 1)}
          >
            Next
          </Button>
        </Flex>
      ) : null}
    </AdminLayout>
  );
};

export default AdminStores;
